import React, { useCallback, useState, useEffect } from 'react';

import {
    ENTITY_ACTIVITIES_FILTERS_OPTIONS,
    FEATURE_FLAGS_NAMES,
    HTTP_OPTIONS,
    INVESTIGATION_ENTITY_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import { httpGet } from '../../../utils';
import FiltersMenu from '../../../Controls/Filters/FiltersMenu';
import { EXTERNAL } from '../../../Controls/Filters/filterTypes';
import {
    ENTITY_ACTIVITIES_FILTERS,
    activitiesType,
    destination,
    location,
    protocol,
    range,
    riskIndicator,
    users
} from '../../../Controls/Filters/FiltersObjects';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import useFeature from '../../../SystemSettings/hooks/featureFlags/useFeature';

const EntityActivitiesFilters = ({ entityType, onFiltersChanged, geoLocationEnabled, lightMode, newDesign }) => {
    const [filters, setFilters] = useState({ range: ENTITY_ACTIVITIES_FILTERS.range });
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);

    const setFiltersData = useCallback(async () => {
        const tempFilters = { ...ENTITY_ACTIVITIES_FILTERS };
        if (entityType.id === INVESTIGATION_ENTITY_TYPES.USER.id) {
            delete tempFilters[users.id];
        } else if (entityType.id === INVESTIGATION_ENTITY_TYPES.RESOURCE.id) {
            delete tempFilters[destination.id];
        } else if (entityType.id === INVESTIGATION_ENTITY_TYPES.INCIDENT.id) {
            delete tempFilters[range.id];
        }

        if (entityType.id !== INVESTIGATION_ENTITY_TYPES.INCIDENT.id) {
            tempFilters[activitiesType.id].filterConfig.options = activitiesType.filterConfig.options.filter(
                (option) => option.value !== ENTITY_ACTIVITIES_FILTERS_OPTIONS.INCIDENT_CHANGES.value
            );
        } else {
            tempFilters[activitiesType.id].value = [ENTITY_ACTIVITIES_FILTERS_OPTIONS.AUTHENTICATIONS.value,
                ENTITY_ACTIVITIES_FILTERS_OPTIONS.EVENTS_LOG.value];
        }

        if (!geoLocationEnabled || entityType.id === INVESTIGATION_ENTITY_TYPES.INCIDENT.id) {
            delete tempFilters[location.id];
        }

        const response = await httpGet('/investigate/authenticationsFiltersOptions', {
            [HTTP_OPTIONS.HANDLE_ERRORS]: true
        });
        if (response.data) {
            tempFilters[riskIndicator.id].filterConfig.options = response.data.riskIndicatorOptions;

            if (systemMode === SYSTEM_MODES.ON_PREM) {
                tempFilters[protocol.id].filterConfig.options = response.data.authProtocolOptions;
            } else {
                tempFilters[protocol.id].filterConfig.options = response.data.authProtocolOptions.filter(
                    (option) => option.value !== 'RADIUS'
                );
            }
        }
        setFilters(tempFilters);
    }, [entityType.id, geoLocationEnabled]);

    useEffect(() => {
        setFiltersData();
    }, [setFiltersData]);

    const filterValueChangedHandler = (filterId, newValue, isExcluded = false) => {
        const newFilters = { ...filters };
        newFilters[filterId] = { ...newFilters[filterId] };

        if (newFilters[filterId].filterType === EXTERNAL) {
            const newEntities = newValue.objectEntities || [];
            newFilters[filterId].value = newEntities.map((entity) => {
                return { label: entity.name || entity.display_name, value: entity.guid };
            });
            newFilters[filterId].externalSelections = { entities: newValue.objectEntities };
        } else {
            newFilters[filterId].value = newValue !== null ? newValue : newFilters[filterId].defaultValue;
        }

        newFilters[filterId].isExcluded = isExcluded;
        setFilters(newFilters);
    };

    const buildFilterValues = useCallback((filters) => {
        const processedFiltersValues = {};
        for (const filter of Object.values(filters)) {
            const isExcludedArray = Array.isArray(filter.value) && filter.isExcluded && filter.value.length !== 0;
            const hasValue = Array.isArray(filter.value) ? filter.value.length : filter.value;
            if (isExcludedArray) {
                const unselectedValues = [];
                for (const option of filter.filterConfig.options) {
                    if (!filter.value.find((val) => val === option.value)) {
                        unselectedValues.push(option.value);
                    }
                }
                processedFiltersValues[filter.id] = unselectedValues;
            } else if (hasValue) {
                processedFiltersValues[filter.id] = filter.value;
            }
        }

        return processedFiltersValues;
    }, []);

    useEffect(() => {
        const filterToSend = buildFilterValues(filters);
        onFiltersChanged(filterToSend);
    }, [onFiltersChanged, buildFilterValues, filters]);

    return (
        <>
            <FiltersMenu
                newDesign={newDesign}
                filters={Object.values(filters)}
                onValueChange={filterValueChangedHandler}
                darkMode={!lightMode}
            />
        </>
    );
};

export default EntityActivitiesFilters;
